/**
 * useWebSocket — Real-time WebSocket hook with exponential backoff reconnection.
 *
 * Features:
 *  - Auto-connects on mount, auto-disconnects on unmount
 *  - Exponential backoff: 1s → 2s → 4s → 8s → max 30s
 *  - Heartbeat ping every 25s to keep connection alive
 *  - Emits status: 'connecting' | 'connected' | 'reconnecting' | 'offline'
 *  - Returns lastEvent so components can react to real-time pushes
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { WS_BASE } from "../lib/api";

const MIN_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;
const PING_INTERVAL_MS = 25000;

export function useWebSocket(restaurantId) {
  const [status, setStatus] = useState("connecting");
  const [lastEvent, setLastEvent] = useState(null);

  const wsRef = useRef(null);
  const retryDelayRef = useRef(MIN_RETRY_MS);
  const retryTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const mountedRef = useRef(true);

  const clearTimers = () => {
    clearTimeout(retryTimerRef.current);
    clearInterval(pingTimerRef.current);
  };

  const connect = useCallback(() => {
    if (!restaurantId || !WS_BASE || !mountedRef.current) return;

    clearTimers();
    setStatus("connecting");

    const url = `${WS_BASE}/ws/updates/${restaurantId}`;
    const socket = new WebSocket(url);
    wsRef.current = socket;

    socket.onopen = () => {
      if (!mountedRef.current) { socket.close(); return; }
      setStatus("connected");
      retryDelayRef.current = MIN_RETRY_MS; // reset backoff on success

      // Heartbeat — prevents proxy timeouts (Kubernetes ingress = 60s idle)
      pingTimerRef.current = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) socket.send("ping");
      }, PING_INTERVAL_MS);
    };

    socket.onmessage = (e) => {
      if (e.data === "pong") return; // heartbeat response — ignore
      try {
        const event = JSON.parse(e.data);
        setLastEvent(event);
      } catch {
        // non-JSON frame — ignore
      }
    };

    socket.onclose = () => {
      clearInterval(pingTimerRef.current);
      if (!mountedRef.current) return;

      setStatus("reconnecting");
      retryTimerRef.current = setTimeout(() => {
        retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_MS);
        connect();
      }, retryDelayRef.current);
    };

    socket.onerror = () => {
      setStatus("offline");
      socket.close();
    };
  }, [restaurantId]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimers();
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((data) => {
    const socket = wsRef.current;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(typeof data === "string" ? data : JSON.stringify(data));
    }
  }, []);

  return { status, lastEvent, send };
}
